import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

type ArticleCardProps = {
  article: Article;
  categoryName: string;
  variant?: "vertical" | "horizontal" | "overlay";
  rank?: number;
};

function MetaRow({ article, muted = false }: { article: Article; muted?: boolean }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${
        muted ? "text-background-300" : "text-foreground-500"
      }`}
    >
      <span className="flex items-center gap-1">
        <i className="ri-calendar-line" />
        {article.publishedAt}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-time-line" />
        {article.readTime} 分钟阅读
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-fire-line" />
        {article.heat.toLocaleString()}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, categoryName, variant = "vertical", rank }: ArticleCardProps) {
  const categoryTag = (
    <Link
      to={`/materials?cat=${article.categoryId.replace("cat-", "")}`}
      className="inline-flex items-center rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
    >
      {categoryName}
    </Link>
  );

  if (variant === "overlay") {
    return (
      <article className="group relative flex h-full min-h-[320px] w-full flex-col justify-end overflow-hidden rounded-lg md:min-h-[420px]">
        <img
          src={article.cover}
          alt={`${article.title} 话题配图`}
          title={`${article.title} - 看料88热点话题`}
          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent" />
        <div className="relative z-10 p-5 md:p-8">
          <span className="inline-flex items-center rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
            {categoryName}
          </span>
          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
            <Link to={`/material/${article.slug}`} className="transition-colors hover:text-primary-200">
              {article.title}
            </Link>
          </h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200">{article.excerpt}</p>
          <div className="mt-4">
            <MetaRow article={article} muted />
          </div>
        </div>
      </article>
    );
  }

  if (variant === "horizontal") {
    return (
      <article className="group flex gap-4 border-b border-background-200 pb-5 last:border-b-0 last:pb-0">
        {typeof rank === "number" && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-foreground-950 font-heading text-sm font-bold text-background-50">
            {rank}
          </span>
        )}
        <Link
          to={`/material/${article.slug}`}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md sm:h-24 sm:w-36"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={`${article.title} 话题配图`}
            title={`${article.title} - 看料88热点话题`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-col">
          <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-950 md:text-base">
            <Link to={`/material/${article.slug}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-1 line-clamp-2 hidden text-xs leading-relaxed text-foreground-600 sm:block">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-2">
            <MetaRow article={article} />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-background-300">
      <Link
        to={`/material/${article.slug}`}
        className="relative block h-44 w-full overflow-hidden"
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={`${article.title} 话题配图`}
          title={`${article.title} - 看料88热点话题`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          {categoryTag}
          {typeof rank === "number" && (
            <span className="font-heading text-xs font-bold text-accent-600">TOP {rank}</span>
          )}
        </div>
        <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/material/${article.slug}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {article.tags.map((tag) => (
            <span key={tag} className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600">
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-4 border-t border-background-200 pt-3">
          <MetaRow article={article} />
        </div>
      </div>
    </article>
  );
}